import { useState, useEffect, useCallback } from 'react';
import { flujoCajaApi } from '../lib/api';
import { AlegraSyncButton } from '../components/AlegraSyncButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from '../hooks/use-toast';
import FlujoCajaGrid from '../components/flujo-caja/FlujoCajaGrid';
import BancosPanel from '../components/flujo-caja/BancosPanel';
import PagosAnuladosBanner from '../components/flujo-caja/PagosAnuladosBanner';
import FirstTimeHint from '../components/FirstTimeHint';
import {
  Landmark,
  Calendar,
  DollarSign,
  RefreshCw,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Loader2,
  Filter,
  ChevronDown,
  ChevronRight,
  Database,
} from 'lucide-react';
import api from '../lib/api';

const formatCLP = (value) => {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);
};

export default function FlujoCajaPage() {
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [vencimientos, setVencimientos] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');

  // CxC sync state
  const [cxcSync, setCxcSync] = useState({ timestamp: null, stale_hours: null, syncing: false });
  const STALE_THRESHOLD_HOURS = 6;

  // Panel collapse state (persisted)
  const [openBancos, setOpenBancos] = useState(() => {
    const v = localStorage.getItem('fc-panel-bancos');
    return v === null ? true : v === '1';
  });
  useEffect(() => { localStorage.setItem('fc-panel-bancos', openBancos ? '1' : '0'); }, [openBancos]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [resumenRes, vencRes] = await Promise.all([
        flujoCajaApi.getResumen(),
        flujoCajaApi.getVencimientos(filtroTipo),
      ]);
      setResumen(resumenRes.data);
      setVencimientos(vencRes.data.vencimientos || []);
    } catch {
      toast({ title: 'Error al cargar flujo de caja', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filtroTipo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load last fc_cxc sync timestamp + auto-trigger if stale (> 6h)
  const loadCxcSyncStatus = useCallback(async (autoTriggerIfStale = true) => {
    try {
      const res = await api.get('/flujo-caja/cxc/sync-status');
      const data = res.data || {};
      setCxcSync(prev => ({ ...prev, timestamp: data.timestamp, stale_hours: data.stale_hours }));
      const isStale =
        data.timestamp == null ||
        (typeof data.stale_hours === 'number' && data.stale_hours >= STALE_THRESHOLD_HOURS);
      if (autoTriggerIfStale && isStale) {
        // Fire-and-forget background sync
        toast({
          title: 'Actualizando CxC desde Alegra…',
          description: data.timestamp
            ? `Última actualización hace ${Math.round(data.stale_hours)} h.`
            : 'Sin datos previos de sincronización.',
        });
        syncCxCNow(/*silent=*/ true);
      }
    } catch (err) {
      // silent fail — sync status is informational only
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncCxCNow = useCallback(async (silent = false) => {
    setCxcSync(prev => ({ ...prev, syncing: true }));
    try {
      const res = await api.post('/flujo-caja/cxc/sync-now');
      const { inserted = 0, updated = 0, closed = 0, timestamp } = res.data || {};
      setCxcSync({ timestamp, stale_hours: 0, syncing: false });
      if (!silent) {
        toast({
          title: 'CxC actualizadas desde Alegra',
          description: `${inserted} nuevas, ${updated} actualizadas, ${closed} cerradas (cobradas).`,
        });
      } else {
        toast({
          title: 'CxC sincronizadas',
          description: `+${inserted} nuevas · ${updated} actualizadas · ${closed} cerradas`,
        });
      }
      // Reload grid + resumen so the user sees fresh numbers
      loadData();
    } catch (err) {
      setCxcSync(prev => ({ ...prev, syncing: false }));
      toast({
        title: 'Error al sincronizar CxC',
        description: err?.response?.data?.detail || err.message || '',
        variant: 'destructive',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCxcSyncStatus(true);
  }, [loadCxcSyncStatus]);

  if (loading && !resumen) {
    return (
      <div className="space-y-6 p-6" data-testid="flujo-caja-loading">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const totalBancos = resumen?.total_bancos || 0;
  const porCobrar = resumen?.por_cobrar || { total: 0, cantidad: 0 };
  const porPagar = resumen?.por_pagar || { total: 0, cantidad: 0 };
  const saldos = resumen?.saldos_bancos || [];

  return (
    <div className="space-y-6 p-6" data-testid="flujo-caja-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-emerald-600" /> Flujo de Caja
          </h1>
          <p className="text-sm text-muted-foreground">Saldos bancarios y vencimientos</p>
        </div>
        <div className="flex items-center gap-2">
          <AlegraSyncButton onSuccess={() => syncCxCNow(false)} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncCxCNow(false)}
            disabled={cxcSync.syncing}
            data-testid="btn-sync-cxc-alegra"
            title={
              cxcSync.timestamp
                ? `Última: ${new Date(cxcSync.timestamp).toLocaleString('es-CL')}${
                    typeof cxcSync.stale_hours === 'number'
                      ? ` (hace ${cxcSync.stale_hours.toFixed(1)} h)`
                      : ''
                  }`
                : 'Nunca sincronizado'
            }
          >
            {cxcSync.syncing
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <Database className="w-4 h-4 mr-2" />}
            Refrescar CxC desde Alegra
            {typeof cxcSync.stale_hours === 'number' && cxcSync.stale_hours >= STALE_THRESHOLD_HOURS && (
              <span className="ml-2 inline-block w-2 h-2 rounded-full bg-amber-500" title="Datos desactualizados" />
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading} data-testid="btn-refresh-flujo">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <FirstTimeHint
        hintId="flujo-caja-grid-editable"
        title="Tip: edita el flujo directamente en el grid"
        description="Haz clic en cualquier celda del grid de Flujo de Caja para editar una provisión. Los cambios se guardan automáticamente y recalculan el saldo proyectado de los próximos 12 meses."
      />

      {/* Iter 286.w — Banner de pagos anulados detectados por el reconciliador */}
      <PagosAnuladosBanner />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="card-total-bancos">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saldo en Bancos</CardTitle>
            <Landmark className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{formatCLP(totalBancos)}</div>
            <p className="text-xs text-muted-foreground mt-1">{saldos.length} cuenta{saldos.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>

        <Card data-testid="card-por-cobrar">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Por Cobrar</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{formatCLP(porCobrar.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">{porCobrar.cantidad} factura{porCobrar.cantidad !== 1 ? 's' : ''} pendiente{porCobrar.cantidad !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>

        <Card data-testid="card-por-pagar">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Por Pagar</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{formatCLP(porPagar.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">{porPagar.cantidad} factura{porPagar.cantidad !== 1 ? 's' : ''} pendiente{porPagar.cantidad !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
      </div>

      {/* Saldos Bancarios (editables inline) — colapsable */}
      <Collapsible open={openBancos} onOpenChange={setOpenBancos}>
        <div className="flex items-center justify-between px-1 mb-1">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-7 px-2 text-xs font-medium"
              data-testid="btn-toggle-bancos"
            >
              {openBancos ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              <Landmark className="h-3.5 w-3.5" />
              Detalle Cuentas Bancarias
              {!openBancos && (
                <span className="ml-2 text-muted-foreground font-normal">
                  ({formatCLP(totalBancos)} · {saldos.length} cta{saldos.length !== 1 ? 's' : ''})
                </span>
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <BancosPanel />
        </CollapsibleContent>
      </Collapsible>

      {/* Grid Día a Día (cuadro del flujo — fuente: MongoDB)
          Incluye botones integrados en el header para abrir Provisiones, Proyección 12m y Vencimientos */}
      <FlujoCajaGrid />
    </div>
  );
}
